import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import UserPdfView from './pages/UserPdfView';
import Register from './pages/Register';
import FormBuilder from './pages/FormBuilder';
import FormList from './pages/FormList';
import SubmissionsList from './pages/SubmissionsList';
import FormView from './pages/FormView';
import FormSubmit from './pages/FormSubmit';
import PublicFormView from './pages/PublicFormView';
import EditSubmission from './pages/EditSubmission';
import ViewSubmission from './pages/ViewSubmission';
import PaymentPage from './pages/PaymentPage';
import NotFound from './components/NotFound';
import SubmissionResultPage from './pages/SubmissionResultPage';
import EditSubmissionPage from './pages/EditSubmissionPage';
import BkashCallbackPage from './pages/BkashCallbackPage';
import RetryPaymentPage from './pages/RetryPaymentPage';
import AdminSubmissions from './pages/admin/AdminSubmissions';
import AdminSubmissionView from './pages/admin/AdminSubmissionView';
import AdminSubmissionEdit from './pages/admin/AdminSubmissionEdit';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import AdminProfile from './pages/admin/AdminProfile';
import Settings from './pages/Settings';
import IndexPage from './pages/IndexPage';
import ProtectedRoute from './components/ProtectedRoute';
// import PermissionGuard from './components/PermissionGuard';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SnackbarProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              {/* <Route path="/register" element={<Register />} /> */}
              {/* <Route path="/form/:id" element={<FormView />} />
              <Route path="/submit/:id" element={<FormSubmit />} /> */}
              
              {/* Edit submission page */}
              <Route path="/edit" element={<EditSubmissionPage />} />
              
              {/* Edit submission route */}
              <Route path="/edit-submission/:uniqueId/:editCode" element={<EditSubmission />} />
              
              {/* View submission route */}
              <Route path="/view-submission/:uniqueId" element={<ViewSubmission />} />
              
              {/* Public form routes with custom URLs */}
              <Route path="/bkash/callback" element={<BkashCallbackPage />} />
              <Route path="/retry-payment/:uniqueId/:paymentID" element={<RetryPaymentPage />} />
              <Route path="/payment/:customUrl" element={<PaymentPage />} />
              <Route path="/success/:customUrl" element={<SubmissionResultPage />} />
              <Route path="/error/:customUrl" element={<SubmissionResultPage />} />
              <Route path="/pdf/:uniqueId" element={<UserPdfView />} />
              <Route path="/:customUrl" element={<PublicFormView />} />
              
              {/* Protected admin routes */}
              <Route path="/admin" element={
                <ProtectedRoute adminOnly>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<AdminProfile />} />
                
                {/* Forms routes - require form permissions */}
                <Route path="forms" element={
                  <ProtectedRoute requiresCapability="canCreateForms">
                    <FormList />
                  </ProtectedRoute>
                } />
                <Route path="forms/new" element={
                  <ProtectedRoute requiresCapability="canCreateForms">
                    <FormBuilder />
                  </ProtectedRoute>
                } />
                <Route path="forms/:id/edit" element={
                  <ProtectedRoute requiresCapability="canEditForms">
                    <FormBuilder />
                  </ProtectedRoute>
                } />
                <Route path="forms/:id/view" element={
                  <ProtectedRoute requiresCapability="canViewSubmissions">
                    <FormView />
                  </ProtectedRoute>
                } />
                <Route path="forms/:id/submissions" element={
                  <ProtectedRoute requiresCapability="canViewSubmissions">
                    <SubmissionsList />
                  </ProtectedRoute>
                } />
                
                {/* Submissions routes - require submission permissions */}
                <Route path="submissions" element={
                  <ProtectedRoute requiresCapability="canViewSubmissions">
                    <AdminSubmissions />
                  </ProtectedRoute>
                } />
                <Route path="submissions/:id" element={
                  <ProtectedRoute requiresCapability="canViewSubmissions">
                    <AdminSubmissionView />
                  </ProtectedRoute>
                } />
                <Route path="submissions/:id/edit" element={
                  <ProtectedRoute requiresCapability="canEditSubmissions">
                    <AdminSubmissionEdit />
                  </ProtectedRoute>
                } />
                
                {/* User management - require user management permissions */}
                <Route path="users" element={
                  <ProtectedRoute requiresCapability="canManageUsers">
                    <AdminUserManagement />
                  </ProtectedRoute>
                } />
                
                {/* Settings - require settings management permissions */}
                <Route path="settings" element={
                  <ProtectedRoute requiresCapability="canManageSettings">
                    <Settings />
                  </ProtectedRoute>
                } />
              </Route>
              
              {/* Index/Landing page */}
              <Route path="/" element={<IndexPage />} />
              
              {/* 404 catch-all route - must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
